import { EVENTS_TYPES_CONTENT, REPLACE_KEYS } from 'constants/history-event-type.constants';
import { IRelevantInfo, IShipmentDestinationInfo } from 'types/suscription/history.types';
import { ILogisticServiceHistoryEvent } from 'types/suscription/history.types';
import { IOrderHistoryEvent } from 'types/suscription/history.types';

const ADDRESS_ISSUE_LOCATION_LABELS: Record<string, string> = {
  origin: 'Origen',
  destination: 'Destino'
};

/**
 * Resuelve el nombre visible de una ubicación corregida para el historial.
 * @param contactName - Nombre de contacto persistido en el evento
 * @param addressIssueLocation - Ubicación técnica del reporte (origin/destination)
 * @returns Nombre de contacto o label de origen/destino
 */
const resolveAddressCorrectionLocationDisplayName = (
  contactName?: string,
  addressIssueLocation?: string
): string => {
  const trimmedContactName = contactName?.trim();
  if (trimmedContactName) return trimmedContactName;  

  const normalizedLocation = addressIssueLocation?.trim().toLowerCase();
  if (normalizedLocation && ADDRESS_ISSUE_LOCATION_LABELS[normalizedLocation]) {
    return ADDRESS_ISSUE_LOCATION_LABELS[normalizedLocation];
  }

  return 'Ubicación no identificada';
};

/**
 * Reemplaza el placeholder de destino en un texto usando destinationPlace, receiverName y destinationCountryName
 * @param destination - Información del destino
 * @returns Texto con el placeholder de destino reemplazado
 */
export const replaceDestinationPlaceholder = (destination: IShipmentDestinationInfo) => {
  const { destinationPlace, receiverName, destinationCountryName } = destination;
  if (destinationPlace === 'user') return receiverName;
  if (destinationPlace === 'office') return `Oficinas ${receiverName}`;
  if (destinationPlace === 'nudos') return `Bodegas ${destinationCountryName}`;
  return '';
};

/**
 * Parsea el campo details de un evento de corrección de dirección reportada.
 * @param details - Contenido serializado del evento
 * @returns Metadata parseada o null si el formato es inválido
 */
export const parseAddressCorrectionHistoryDetails = (
  details?: string | null
): { location?: string; locationName?: string; correctedFieldKeys?: string[] } | null => {
  if (!details?.trim()) return null;

  try {
    const parsed = JSON.parse(details) as {
      location?: string;
      locationName?: string;
      correctedFieldKeys?: string[];
    };

    if (
      !parsed?.location ||
      typeof parsed.locationName !== 'string' ||
      !Array.isArray(parsed?.correctedFieldKeys)
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Parsea el campo details de un evento de cierre de reporte de incidentes.
 * @param details - Contenido serializado del evento
 * @returns Metadata parseada o null si el formato es inválido
 */
export const parseClosedIncidentReportHistoryDetails = (
  details?: string | null
): { reportName?: string } | null => {
  if (!details?.trim()) return null;

  try {
    const parsed = JSON.parse(details) as { reportName?: string };

    if (typeof parsed?.reportName !== 'string') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Reemplaza todos los placeholders de EVENTS_TYPES_CONTENT en un texto usando relevantInfo
 * @param text - Texto que puede contener placeholders [usuario] y [destino]
 * @param relevantInfo - Información relevante que contiene userId, userName y destinationName
 * @returns Texto con todos los placeholders reemplazados y capitalizado
 */
export const replaceEventPlaceholders = (
  text: string,
  userId?: number | null,
  relevantInfo?: IRelevantInfo
): string => {
  // Definir el placeholder de usuario
  let userReplacement = 'Usuario no identificado';
  if (userId !== null && userId !== undefined && [0, 1].includes(userId)) {
    userReplacement = 'Sistema';
  }
  if (relevantInfo?.responsible) {
    userReplacement = relevantInfo.responsible.fullNameOrEmail;
  }
  // Definir el placeholder de destino
  const destinationReplacement = relevantInfo?.destination
    ? replaceDestinationPlaceholder(relevantInfo.destination)
    : 'Destino no identificado';

  const locationNameReplacement = resolveAddressCorrectionLocationDisplayName(
    relevantInfo?.locationName,
    relevantInfo?.location
  );

  const reportNameReplacement =
    relevantInfo?.reportName?.trim() || 'Reporte sin título';

  // Definir el placeholder de serial anterior
  const oldProductSerialReplacement = relevantInfo?.oldProductSerial || 'N/A';

  // Definir el placeholder de serial nuevo
  const newProductSerialReplacement = relevantInfo?.newProductSerial || 'N/A';

  // Aplicar los reemplazos
  let replacedText = text.replace(REPLACE_KEYS.USER, userReplacement);
  replacedText = replacedText.replace(REPLACE_KEYS.DESTINATION, destinationReplacement);
  replacedText = replacedText.replace(
    REPLACE_KEYS.LOCATION_NAME,
    locationNameReplacement
  );
  replacedText = replacedText.replace(REPLACE_KEYS.REPORT_NAME, reportNameReplacement);
  replacedText = replacedText.replace(REPLACE_KEYS.OLD_PRODUCT_SERIAL, oldProductSerialReplacement);
  replacedText = replacedText.replace(REPLACE_KEYS.NEW_PRODUCT_SERIAL, newProductSerialReplacement);
  
  // Capitalizar la primera letra del texto
  const capitalized = replacedText.charAt(0).toUpperCase() + replacedText.slice(1);
  return capitalized;
};

interface IEventContent {
  name: string | null;
  description: string | null;
}

/**
 * Mapper para obtener el nombre y la descripción de un evento basado en su UUID
 * @param eventUuid - UUID del evento
 * @returns Objeto con name y description, o valores por defecto si no se encuentra
 */
export const getEventContentByUuid = (eventUuid: string): IEventContent => {
  const initialValue = { name: null, description: null };
  if (!eventUuid) return initialValue;
  const event = EVENTS_TYPES_CONTENT[eventUuid as keyof typeof EVENTS_TYPES_CONTENT];
  if (!event) return initialValue;
  return { name: event.title, description: event.subtitle };
};

/**
 * Modifica el contenido de un evento basado en su UUID
 * @param event - Evento a modificar
 * @returns Objeto con name y description, o valores por defecto si no se encuentra
 */
export const modifyEventContent = (event: ILogisticServiceHistoryEvent | IOrderHistoryEvent): IEventContent => {
  const { details, relevantInfo, userId, eventType } = event;
  const parsedAddressCorrectionDetails = parseAddressCorrectionHistoryDetails(details);
  const parsedClosedIncidentReportDetails =
    parseClosedIncidentReportHistoryDetails(details);
  const { location, locationName, correctedFieldKeys, reportName } =
    relevantInfo ?? {};

  const {
    location: parsedLocation,
    locationName: parsedLocationName,
    correctedFieldKeys: parsedCorrectedFieldKeys
  } = parsedAddressCorrectionDetails ?? {};

  const { reportName: parsedReportName } = parsedClosedIncidentReportDetails ?? {};

  const enrichedRelevantInfo: IRelevantInfo = {
    ...relevantInfo,
    location: location ?? parsedLocation,
    locationName: locationName ?? parsedLocationName,
    correctedFieldKeys: correctedFieldKeys ?? parsedCorrectedFieldKeys,
    reportName: reportName ?? parsedReportName
  };

  const eventContent = getEventContentByUuid(eventType?.uuid || '');
  const name = eventContent.name || eventType?.name || '';
  const description = replaceEventPlaceholders(
    eventContent.description || '',
    userId,
    enrichedRelevantInfo
  );

  return { name, description };
};
